<script setup lang="ts">
import { computed, useAttrs, type HTMLAttributes } from 'vue';
import { useFieldControlId } from './fieldContext';
import LumiIcon from '../icons/LumiIcon.vue';

defineOptions({ inheritAttrs: false });

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

withDefaults(
  defineProps<{
    options?: Array<string | SelectOption>;
    placeholder?: string;
    invalid?: boolean;
    disabled?: boolean;
    /** `sm` is the compact control for a table row or an inline bar (`--control-h-sm`). */
    size?: 'sm' | 'md';
    /**
     * No field chrome: the select reads as the value in a sentence ("Workflow: Standard ▾"),
     * the chevron being the only hint it opens.
     */
    bare?: boolean;
  }>(),
  { options: () => [], invalid: false, disabled: false, size: 'md', bare: false },
);

const model = defineModel<string | number>();
const attrs = useAttrs();
// class/style: the wrapper, which holds the chevron — a width or grid class sizes the
// whole control, not just the native box. Everything else: the native select.
const rootAttrs = computed<HTMLAttributes>(() => ({ class: attrs.class as HTMLAttributes['class'], style: attrs.style as HTMLAttributes['style'] }));
const controlId = useFieldControlId(() => attrs.id as string | undefined);
const selectAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});
</script>

<template>
  <span v-bind="rootAttrs" class="lumi-select-wrap" :class="{ 'lumi-select-wrap--sm': size === 'sm', 'lumi-select-wrap--bare': bare }">
    <select
      v-model="model"
      v-bind="selectAttrs"
      :id="controlId"
      :disabled="disabled"
      class="lumi-select"
      :class="{ 'lumi-select--invalid': invalid, 'lumi-select--sm': size === 'sm', 'lumi-select--bare': bare }"
    >
      <option v-if="placeholder" value="" disabled hidden>{{ placeholder }}</option>
      <template v-if="options.length">
        <option
          v-for="o in options"
          :key="typeof o === 'string' ? o : o.value"
          :value="typeof o === 'string' ? o : o.value"
          :disabled="typeof o === 'string' ? false : o.disabled"
        >
          {{ typeof o === 'string' ? o : o.label }}
        </option>
      </template>
      <slot v-else />
    </select>
    <LumiIcon name="expand" class="lumi-select__chevron" width="16" height="16" stroke-width="2.25" />
  </span>
</template>

<style>
.lumi-select-wrap{position:relative;display:block;}
.lumi-select{display:block;width:100%;height:var(--field-h);padding:0 12px;border:1px solid var(--field-border);border-radius:var(--radius-md);background:var(--field-bg);color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-sm);line-height:1.5;transition:border-color var(--dur-fast);padding-right:36px;appearance:none;-webkit-appearance:none;cursor:pointer;}
.lumi-select:focus{border-color:var(--panel-edge);}
.lumi-select:disabled{background:var(--surface-wash);color:var(--text-secondary);cursor:not-allowed;}
.lumi-select--invalid{border-color:var(--danger);}
.lumi-select--invalid:focus{border-color:var(--danger);}
.lumi-select--invalid:focus-visible{outline-color:var(--danger);}
.lumi-select__chevron{position:absolute;right:12px;top:50%;transform:translateY(-50%);width:16px;height:16px;color:var(--text-secondary);pointer-events:none;}
/* Compact: the row/bar scale, same ground and edge. */
.lumi-select--sm{height:var(--control-h-sm);padding:0 8px;padding-right:28px;font-size:var(--text-xs);}
.lumi-select-wrap--sm .lumi-select__chevron{right:8px;width:14px;height:14px;}
.lumi-select-wrap--bare{display:inline-flex;align-items:center;min-width:0;}
.lumi-select--bare{height:auto;border:0;background:transparent;padding:0 20px 0 0;font-size:var(--text-sm);font-weight:var(--weight-medium);color:var(--text-heading);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.lumi-select-wrap--bare .lumi-select__chevron{right:2px;}
</style>
